import { splitEncoding } from '@contentful/live-preview';

/**
 * Preview responses are passed through `encodeGraphQLResponse`, which appends
 * invisible characters to plain strings so Contentful live preview can map
 * rendered text back to a field. Production never does this.
 *
 * Those characters are harmless inside visible copy. They break values that
 * are interpreted rather than displayed: a `textColor` of `white` becomes
 * invalid CSS, a relative `url` no longer matches a section `id`, and a grid
 * `col` fails to parse. Stripping every string would make preview match
 * production, but live preview would stop working for headings and rich text.
 *
 * So this runs once, immediately after encoding, and cleans only machine
 * fields (colors, urls, ids, enums, layout). Visible text is left encoded.
 * Doing it here covers every consumer. Cleaning at each component, as Heading
 * already does for its own colors, is easy to miss — cards and anchor links
 * were still broken.
 *
 * The same applies to CMS control values that components compare literally:
 * heading variations, product-card variants, CTA styles and urls, and the
 * equivalent style, option, target, and url fields. Visible copy such as
 * `urlText`, headings, and rich text stays encoded.
 */
const cleanEncodedString = (value: string): string =>
  splitEncoding(value).cleaned;

const isMachineField = (key: string): boolean =>
  key === 'id' ||
  key.endsWith('Id') ||
  key === 'type' ||
  key.endsWith('Type') ||
  /color$/i.test(key) ||
  key === 'url' ||
  /^url\d+$/.test(key) ||
  key === 'slug' ||
  key === 'uri' ||
  key === 'href' ||
  /sku$/i.test(key) ||
  key === 'product' ||
  key === 'products' ||
  key === 'col' ||
  key.startsWith('colFor') ||
  key.startsWith('colGap') ||
  key === 'rowGap' ||
  key === 'maxWidth' ||
  key.startsWith('padding') ||
  key.endsWith('Alignment') ||
  key.endsWith('Direction') ||
  key.endsWith('Position') ||
  key.endsWith('Option') ||
  key.endsWith('Style') ||
  key.endsWith('Target') ||
  key.endsWith('Url') ||
  key.endsWith('Variation') ||
  key === 'variant' ||
  key === 'contentMaxWidth';

const isColorValue = (key: string, parentKey: string | undefined): boolean =>
  key === 'value' && !!parentKey && /color$/i.test(parentKey);

const walk = (node: unknown, parentKey: string | undefined): void => {
  if (!node || typeof node !== 'object') {
    return;
  }

  if (Array.isArray(node)) {
    node.forEach((item) => {
      walk(item, parentKey);
    });
    return;
  }

  const record = node as Record<string, unknown>;

  for (const key of Object.keys(record)) {
    const value = record[key];

    if (
      typeof value === 'string' &&
      (isMachineField(key) || isColorValue(key, parentKey))
    ) {
      record[key] = cleanEncodedString(value);
      continue;
    }

    if (Array.isArray(value) && isMachineField(key)) {
      record[key] = value.map((item) =>
        typeof item === 'string' ? cleanEncodedString(item) : item,
      );
    }

    walk(record[key], key);
  }
};

export const cleanPreviewMachineStrings = <T>(data: T): T => {
  walk(data, undefined);
  return data;
};
